/**
 * Shared helpers for department-based filtering.
 *
 * A user can be linked to departments in two ways in the backend:
 *   - `departmentId` / `department`  (primary department, ManyToOne)
 *   - `departments[]`                (user_departments join table, ManyToMany)
 * Filters must consider both, otherwise users assigned only through the join
 * table never appear in (or match) the department filter.
 */

export const getUserDepartmentIds = (user) => {
  if (!user) return [];
  const ids = new Set();
  if (user.departmentId != null) ids.add(String(user.departmentId));
  if (user.department?.id != null) ids.add(String(user.department.id));
  (user.departments || []).forEach((d) => d?.id != null && ids.add(String(d.id)));
  return Array.from(ids);
};

export const getUserDepartmentNames = (user) => {
  if (!user) return [];
  const names = new Set();
  if (user.department?.name) names.add(user.department.name);
  if (user.departmentName) names.add(user.departmentName);
  (user.departments || []).forEach((d) => d?.name && names.add(d.name));
  return Array.from(names);
};

/**
 * Build the department options list: every department returned by the API,
 * plus any department referenced by the given users that the API did not
 * return (e.g. when the current user lacks `department:read`).
 */
export const buildDepartmentOptions = (apiDepartments, users) => {
  const map = new Map();
  (apiDepartments || []).forEach((d) => d?.id != null && map.set(String(d.id), d.name));
  (users || []).forEach((u) => {
    // Primary department (ManyToOne relation object)
    if (u?.department?.id != null && !map.has(String(u.department.id))) {
      map.set(String(u.department.id), u.department.name);
    }
    // Flat departmentId + departmentName (when relation is not populated)
    if (u?.departmentId != null && !map.has(String(u.departmentId))) {
      map.set(String(u.departmentId), u.departmentName || u.department?.name || `#${u.departmentId}`);
    }
    // ManyToMany departments (user_departments join table)
    (u?.departments || []).forEach((d) => {
      if (d?.id != null && !map.has(String(d.id))) map.set(String(d.id), d.name);
    });
  });
  return Array.from(map.entries())
    .map(([id, name]) => ({ id, name: name || `#${id}` }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const userMatchesDepartment = (user, departmentFilter) =>
  departmentFilter === 'all' || getUserDepartmentIds(user).includes(String(departmentFilter));
